import { ChangeDetectionStrategy, Component, computed, model, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Card } from 'primeng/card';
import { Tag } from 'primeng/tag';
import { ToggleSwitch } from 'primeng/toggleswitch';

import {
    IBeforeBookingTriggerData,
    IScheduleEmailActionData,
    IScheduleSmsActionData,
    ISendEmailActionData,
    ISendSmsActionData,
    ITrigger,
    ITriggerActionData,
    ITriggerData,
    TriggerActionName,
    TriggerName
} from '../../../../../../../../../../common/common-interfaces/trigger';

export enum ValidationError {
    NoAction = 'NO_TRIGGER_ACTION_SET',
    NoTriggerEvent = 'NO_TRIGGER_EVENT_SET',
    NoSubject = 'NO_SUBJECT_SET',
    NoSender = 'NO_SENDER_NAME_SET',
    NoReceiver = 'NO_RECEIVER_TYPE_SET',
    NoCustomReceiverEmail = 'NO_CUSTOM_RECEIVER_EMAIL_SET',
    NoCustomReceiverPhone = 'NO_CUSTOM_RECEIVER_PHONE_SET',
    NoTimeValue = 'NO_TIME_VALUE_SET',
    NoTimeUnit = 'NO_TIME_UNIT_SET',
    NoTemplate = 'NO_EMAIL_TEMPLATE_SET',
    NoMessage = 'NO_MESSAGE_SET'
}

type ValidationRule<T> = {
    isValid: (data: T) => boolean;
    error: ValidationError;
};

function validate<T>(data: T, rules: ValidationRule<T>[]): ValidationError | '' {
    for (const rule of rules) {
        if (!rule.isValid(data)) {
            return rule.error;
        }
    }
    return '';
}

const commonReceiverRules = <T extends { receivers?: ('CUSTOMER' | 'CUSTOM')[] }>(
    getCustomValue: (d: T) => string | undefined,
    customError: ValidationError
): ValidationRule<T>[] => [
    {
        isValid: (d) => !!d?.receivers?.length,
        error: ValidationError.NoReceiver
    },
    {
        isValid: (d) => !d?.receivers?.includes('CUSTOM') || !!getCustomValue(d)?.length,
        error: customError
    }
];

const timeRules: ValidationRule<{ timeValue?: number; timeUnit?: string }>[] = [
    {
        isValid: (d) => !!d?.timeValue,
        error: ValidationError.NoTimeValue
    },
    {
        isValid: (d) => !!d?.timeUnit,
        error: ValidationError.NoTimeUnit
    }
];

const baseEmailRules: ValidationRule<ISendEmailActionData>[] = [
    {
        isValid: (d) => !!d?.subject?.length,
        error: ValidationError.NoSubject
    },
    {
        isValid: (d) => !!d?.senderName?.length,
        error: ValidationError.NoSender
    },
    ...commonReceiverRules<ISendEmailActionData>((d) => d?.customReceiverEmail, ValidationError.NoCustomReceiverEmail),
    {
        isValid: (d) => !!(d?.thirdParty?.maillune?.templateHtml?.length || d?.thirdParty?.beefree?.templateHtml?.length),
        error: ValidationError.NoTemplate
    }
];

const scheduleEmailRules: ValidationRule<IScheduleEmailActionData>[] = [
    {
        isValid: (d) => !!d?.subject?.length,
        error: ValidationError.NoSubject
    },
    {
        isValid: (d) => !!d?.senderName?.length,
        error: ValidationError.NoSender
    },
    ...commonReceiverRules<IScheduleEmailActionData>((d) => d?.customReceiverEmail, ValidationError.NoCustomReceiverEmail),
    ...timeRules,
    {
        isValid: (d) => !!(d?.thirdParty?.maillune?.templateHtml?.length || d?.thirdParty?.beefree?.templateHtml?.length),
        error: ValidationError.NoTemplate
    }
];

const baseSmsRules: ValidationRule<ISendSmsActionData>[] = [
    {
        isValid: (d) => !!d?.message?.length,
        error: ValidationError.NoMessage
    },
    ...commonReceiverRules<ISendSmsActionData>((d) => d?.customReceiverPhone, ValidationError.NoCustomReceiverPhone)
];

const scheduleSmsRules: ValidationRule<IScheduleSmsActionData>[] = [
    {
        isValid: (d) => !!d?.message?.length,
        error: ValidationError.NoMessage
    },
    ...commonReceiverRules<IScheduleSmsActionData>((d) => d?.customReceiverPhone, ValidationError.NoCustomReceiverPhone),
    ...timeRules
];

const ACTION_VALIDATORS: Record<TriggerActionName, (actionData: ITriggerActionData) => ValidationError | ''> = {
    SEND_EMAIL: (data) => validate(data.sendEmailData!, baseEmailRules),
    SCHEDULE_EMAIL: (data) => validate(data.scheduleEmailData!, scheduleEmailRules),
    SEND_SMS: (data) => validate(data.sendSmsData!, baseSmsRules),
    SCHEDULE_SMS: (data) => validate(data.scheduleSmsData!, scheduleSmsRules)
};

const TRIGGER_VALIDATORS: Partial<Record<TriggerName, (triggerData: ITriggerData) => ValidationError | ''>> = {
    BEFORE_BOOKING_START: (data) => validate(data.beforeBookingData as IBeforeBookingTriggerData, timeRules),
    BOOKING_CREATED: () => '',
    BOOKING_CHANGED: () => '',
    BOOKING_CANCELLED: () => '',
    BOOKING_CHECKED_IN: () => ''
};

@Component({
    selector: 'trigger-card',
    templateUrl: './trigger-card.html',
    styleUrl: 'trigger-card.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, FormsModule, TranslatePipe, Card, Tag, ToggleSwitch]
})
export class TriggerCardComponent {
    trigger = model.required<ITrigger>();

    cardClicked = output<ITrigger>();
    enabledChanged = output<ITrigger>();

    triggerValid = computed(() => this.triggerErrorMessage().length === 0);
    triggerErrorMessage = computed(() => this.isTriggerValid(this.trigger()));

    onCardClick(): void {
        this.cardClicked.emit(this.trigger());
    }

    onEnabledChange(enabled: boolean): void {
        this.trigger.update((current) => ({ ...current, enabled }));
        this.enabledChanged.emit(this.trigger());
    }

    private isTriggerValid(trigger: ITrigger): string {
        if (!trigger.actionData?.name) {
            return ValidationError.NoAction;
        }

        const actionValidator = ACTION_VALIDATORS[trigger.actionData.name];
        const actionError = actionValidator ? actionValidator(trigger.actionData) : '';
        if (actionError) {
            return actionError;
        }

        if (!trigger.triggerData?.name) {
            return ValidationError.NoTriggerEvent;
        }

        const triggerValidator = TRIGGER_VALIDATORS[trigger.triggerData.name];
        return triggerValidator ? triggerValidator(trigger.triggerData) : '';
    }
}
